// 📄 components/Configuracion/NuevaApiModal.tsx
// Modal de alta Y edición de una API en Configuración > APIs del proyecto
// — antes el alta se armaba inline al final de la lista apenas se elegía
// el tipo (ver ConfiguracionApis.tsx), dejando una API "a medias"
// mezclada con las ya guardadas; la edición pasaba por expandir la card
// en la lista, lo que obligaba a hacer scroll hasta el fondo de un
// formulario largo para llegar a algún botón. Acá nada toca `apis` en
// ConfiguracionApis.tsx hasta tocar "Guardar"/"Guardar cambios" en el
// modal — cancelar nunca deja un draft a medias en la lista ni pisa una
// API existente a medio editar.
//
// "Guardar" persiste directo contra Firestore (mismo patrón que
// IncidenteFormModal.tsx/NotasSapSection.tsx: guardando/error locales,
// botón deshabilitado durante el guardado, error inline sin cerrar el
// modal) — ya no hay botón "Guardar configuración" al final de la página
// (ver ConfiguracionApis.tsx) que persista después. El servicio sigue
// siendo guardarConfigCliente(clienteId, "apis", { apis }) — exige el
// array COMPLETO, así que acá se arma con `apisActuales` (reemplazando o
// agregando según haya `apiInicial`) antes de llamar.
//
// Modo alta: dos pasos dentro del mismo modal — 1) elegir tipo (Ariba/
// S4HANA, del que dependen las credenciales), 2) el formulario completo
// (ApiDefinitionForm.tsx), con un "← Volver" para cambiar el tipo si el
// usuario se equivocó. Modo edición (`apiInicial` presente): el tipo ya
// está fijo (no tiene sentido cambiarlo — cambiaría la forma de las
// credenciales de una API ya configurada), así que arranca directo en el
// paso 2, precargado con los valores actuales, sin el paso de elegir
// tipo ni el "← Volver". Los campos de credenciales (API Key, OAuth
// Secret, Contraseña) llegan vacíos como siempre — nunca vuelven del GET
// — así que ApiDefinitionForm ya muestra el placeholder enmascarado y el
// aviso "ya hay credenciales guardadas" sin ningún cambio adicional acá.
import { useRef, useState } from "react";
import { X, Save, ArrowLeft, RefreshCw } from "lucide-react";
import ConfirmDialog from "../ui/ConfirmDialog.tsx";
import ApiDefinitionForm from "./ApiDefinitionForm.tsx";
import { ApiDefinition, ApiTipoId, guardarConfigCliente } from "../../services/clienteConexionService.ts";
import { ApiTemplate } from "../../services/catalogoApisService.ts";
import { TIPOS_API, TIPOS_API_LISTA } from "./apiTiposConfig.ts";

interface NuevaApiModalProps {
  clienteId: string;
  catalogo: ApiTemplate[];
  // Array completo vigente de APIs del cliente — necesario para armar el
  // payload completo que exige guardarConfigCliente (ver comentario de
  // arriba), no solo la que se está agregando/editando.
  apisActuales: ApiDefinition[];
  // Presente = modo edición (precarga esta API, salta el paso de elegir
  // tipo); ausente = modo alta.
  apiInicial?: ApiDefinition | null;
  // Ya persistido en Firestore — el padre recarga la lista y cierra el
  // modal (mismo patrón que onSaved en IncidenteFormModal.tsx).
  onGuardado: () => void;
  onCancelar: () => void;
}

const generarId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const nuevaApi = (tipo: ApiTipoId): ApiDefinition => ({
  id: generarId(),
  tipo,
  authTipo: TIPOS_API[tipo]?.authTipo || "oauth",
  nombre: "",
  protocolo: "",
  ambiente: "",
  urlBase: "",
  api: "",
  apiKey: "",
  oauthSecret: "",
  clientId: "",
  dataCenter: "",
  realm: "",
  anid: "",
  tokenUrl: "",
  grantType: "",
  usuario: "",
  contraseña: "",
  tieneCredencialesGuardadas: false,
  endpoints: []
});

export default function NuevaApiModal({ clienteId, catalogo, apisActuales, apiInicial, onGuardado, onCancelar }: NuevaApiModalProps) {
  const modoEdicion = !!apiInicial;
  const [tipo, setTipo] = useState<ApiTipoId | null>(apiInicial?.tipo ?? null);
  const [api, setApi] = useState<ApiDefinition | null>(apiInicial ? { ...apiInicial } : null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mostrarConfirmDescartar, setMostrarConfirmDescartar] = useState(false);
  // Qué hacer si el admin confirma que quiere descartar: cerrar el modal
  // entero, o solo volver al paso 1 (cambiar de tipo) — este último no
  // aplica en modo edición, donde no hay paso 1.
  const [accionPendiente, setAccionPendiente] = useState<"cerrar" | "volver" | null>(null);
  // Snapshot del estado "recién elegido el tipo" (alta) o "tal como estaba
  // guardada" (edición), para detectar cambios sin guardar (ver esDirty).
  const apiInicialRef = useRef<ApiDefinition | null>(apiInicial ? { ...apiInicial } : null);

  const tipoMeta = tipo ? TIPOS_API[tipo] : null;
  const esDirty = !!api && JSON.stringify(api) !== JSON.stringify(apiInicialRef.current);

  const elegirTipo = (t: ApiTipoId) => {
    const nueva = nuevaApi(t);
    apiInicialRef.current = nueva;
    setTipo(t);
    setApi(nueva);
  };

  const volverAPaso1 = () => {
    setTipo(null);
    setApi(null);
    apiInicialRef.current = null;
  };

  // Backdrop, X y "Cancelar" pasan por acá: si hay cambios sin guardar en
  // el paso 2, se confirma antes de perderlos. Mientras guardando esté en
  // true no cierra nada — evita abandonar el modal a mitad de un request.
  const intentarCerrar = () => {
    if (guardando) return;
    if (esDirty) {
      setAccionPendiente("cerrar");
      setMostrarConfirmDescartar(true);
      return;
    }
    onCancelar();
  };

  const intentarVolver = () => {
    if (guardando) return;
    if (esDirty) {
      setAccionPendiente("volver");
      setMostrarConfirmDescartar(true);
      return;
    }
    volverAPaso1();
  };

  const handleConfirmarDescarte = () => {
    setMostrarConfirmDescartar(false);
    if (accionPendiente === "volver") volverAPaso1();
    else onCancelar();
    setAccionPendiente(null);
  };

  const handleCancelarDescarte = () => {
    setMostrarConfirmDescartar(false);
    setAccionPendiente(null);
  };

  // Arma el array COMPLETO (guardarConfigCliente sobrescribe todo el
  // documento "apis", ver comentario de cabecera) reemplazando la API en
  // edición o agregando la nueva al final, y persiste directo — ya no hay
  // botón "Guardar configuración" que lo haga después (ver ConfiguracionApis.tsx).
  const handleGuardar = async () => {
    if (!api) return;
    setError(null);
    setGuardando(true);
    try {
      const apisActualizadas = modoEdicion
        ? apisActuales.map((a) => (a.id === api.id ? api : a))
        : [...apisActuales, api];
      await guardarConfigCliente(clienteId, "apis", { apis: apisActualizadas });
      onGuardado();
    } catch (err: any) {
      setError(err.message || "Error al guardar la API.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4"
        onClick={intentarCerrar}
      >
        {/* max-h-[90vh] + flex-col con header/footer flex-shrink-0 y el
            body flex-1 overflow-y-auto: header y footer siempre visibles,
            solo el formulario hace scroll. */}
        <div
          className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              {tipoMeta && <span className="text-xl leading-none">{tipoMeta.icon}</span>}
              {modoEdicion ? "Editar API" : "Nueva API"}{tipoMeta ? ` · ${tipoMeta.label}` : ""}
            </h3>
            <button onClick={intentarCerrar} disabled={guardando} className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5 overflow-y-auto flex-1 space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
            )}

            {!tipo || !api ? (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-300">¿Qué tipo de API vas a configurar?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {TIPOS_API_LISTA.map((tm) => (
                    <button
                      key={tm.id}
                      type="button"
                      onClick={() => elegirTipo(tm.id)}
                      className="text-left bg-black/20 hover:bg-black/30 border border-white/10 hover:border-blue-500/50 rounded-xl p-4 transition-all"
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xl leading-none">{tm.icon}</span>
                        <span className="text-sm font-semibold text-white">{tm.label}</span>
                      </div>
                      <p className="text-xs text-gray-400">{tm.descripcion}</p>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {!modoEdicion && (
                  <button
                    type="button"
                    onClick={intentarVolver}
                    disabled={guardando}
                    className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Volver a elegir tipo
                  </button>
                )}
                <ApiDefinitionForm api={api} editable catalogo={catalogo} onChange={setApi} />
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 p-5 border-t border-white/10 flex-shrink-0">
            <button
              type="button"
              onClick={intentarCerrar}
              disabled={guardando}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancelar
            </button>
            {tipo && api && (
              <button
                type="button"
                onClick={handleGuardar}
                disabled={guardando}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
              >
                {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {guardando ? "Guardando..." : modoEdicion ? "Guardar cambios" : "Guardar"}
              </button>
            )}
          </div>
        </div>
      </div>

      {mostrarConfirmDescartar && (
        <ConfirmDialog
          title="¿Descartar los cambios?"
          message="Todavía no guardaste esta API — si descartas, se pierde lo que escribiste."
          confirmLabel="Sí, descartar"
          cancelLabel="Seguir editando"
          danger
          onConfirm={handleConfirmarDescarte}
          onCancel={handleCancelarDescarte}
        />
      )}
    </>
  );
}
